import Layout from "@/components/Layout";
import PageHead from "@/components/PageHead";
import AnimatedSection from "@/components/AnimatedSection";

const analyses = [
  {
    industry: "Technology & Digital Platforms",
    overview: "The technology sector encompasses digital platforms, software markets, cloud computing, and adjacent digital services. Antitrust analysis in this sector increasingly focuses on multi-sided platform dynamics and network effects.",
    marketDefinition: "Market definition in technology markets often involves complex questions about platform boundaries, interoperability, and the competitive significance of data assets.",
    dynamics: "Strong network effects, high switching costs, and data-driven competitive advantages create unique competitive dynamics that can lead to market concentration.",
    concerns: "Regulators focus on platform self-preferencing, killer acquisitions of nascent competitors, interoperability barriers, and data-driven competitive advantages.",
    examples: "DOJ and FTC have pursued multiple enforcement actions against major technology platforms, challenging both acquisitions and conduct.",
  },
  {
    industry: "Life Sciences & Pharmaceuticals",
    overview: "The pharmaceutical industry involves complex competitive dynamics including patent protection, FDA regulatory pathways, and innovation competition among pipeline products.",
    marketDefinition: "Relevant markets often defined narrowly around specific therapeutic categories or drug classes. Innovation competition markets are increasingly recognized.",
    dynamics: "Patent lifecycles, generic and biosimilar entry, and pipeline competition create distinctive competitive patterns.",
    concerns: "Reverse payment settlements, product hopping, sham litigation to delay generic entry, and merger-related pipeline overlaps.",
    examples: "FTC has actively challenged pharmaceutical mergers and pursued enforcement actions against pay-for-delay settlements.",
  },
  {
    industry: "Healthcare Services",
    overview: "Healthcare services markets encompass hospitals, health systems, physician practices, insurance, and ancillary healthcare services.",
    marketDefinition: "Geographic market definition is critical in healthcare, often centering on patient travel patterns and payer negotiating dynamics.",
    dynamics: "Provider consolidation, vertical integration between providers and insurers, and the growing role of private equity in healthcare.",
    concerns: "Hospital mergers in concentrated markets, cross-market mergers, and effects on healthcare pricing and quality.",
    examples: "FTC has challenged multiple hospital and healthcare system mergers in recent years.",
  },
  {
    industry: "Financial Services & Fintech",
    overview: "Financial services encompasses banking, insurance, investment management, and emerging fintech platforms.",
    marketDefinition: "Markets often defined around specific financial products or services, with geographic considerations varying by product.",
    dynamics: "Regulatory barriers to entry, fintech disruption, and the competitive significance of data and technology platforms.",
    concerns: "Bank merger review under competitive effects standards, fintech acquisition patterns, and data-driven competitive advantages.",
    examples: "DOJ reviews bank mergers under competitive effects standards, and regulators are increasingly examining fintech acquisitions.",
  },
];

const MarketStructure = () => (
  <Layout>
    <PageHead title="Market Structure Analysis" description="Alicia J. Batts analyzes how regulators and courts assess market competition across technology, life sciences, healthcare, and financial services for antitrust cases." path="/market-structure" keywords="market structure analysis antitrust, Alicia Batts market analysis, competition analysis, relevant market definition, antitrust market structure" />
    <section className="bg-hero text-primary-foreground py-20">
      <div className="editorial-container">
        <AnimatedSection animation="fade"><div className="gold-accent-line mb-6" /></AnimatedSection>
        <AnimatedSection animation="fade-up" delay={100}><h1 className="text-4xl md:text-5xl font-serif font-semibold mb-4">Market Structure Analysis</h1></AnimatedSection>
        <AnimatedSection animation="fade-up" delay={200}><p className="text-lg opacity-80 max-w-2xl">Understanding how regulators and courts analyze market competition across industries—essential context for corporate strategy and transaction planning.</p></AnimatedSection>
      </div>
    </section>

    <section className="section-padding">
      <div className="editorial-container space-y-16">
        {analyses.map((a, i) => (
          <AnimatedSection key={a.industry} animation="fade-up" delay={i * 80}>
          <div key={a.industry} className="bg-card rounded-lg p-8 md:p-10 shadow-card border border-border">
            <h2 className="font-serif text-2xl font-semibold text-foreground mb-8">{a.industry}</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {[
                { label: "Industry Overview", content: a.overview },
                { label: "Market Definition Issues", content: a.marketDefinition },
                { label: "Competitive Dynamics", content: a.dynamics },
                { label: "Common Antitrust Concerns", content: a.concerns },
              ].map((s) => (
                <div key={s.label}>
                  <p className="text-xs font-semibold uppercase tracking-widest text-gold mb-2">{s.label}</p>
                  <p className="text-muted-foreground text-sm leading-relaxed">{s.content}</p>
                </div>
              ))}
              <div className="md:col-span-2">
                <p className="text-xs font-semibold uppercase tracking-widest text-gold mb-2">Enforcement Examples</p>
                <p className="text-muted-foreground text-sm leading-relaxed">{a.examples}</p>
              </div>
            </div>
          </div>
          </AnimatedSection>
        ))}
      </div>
    </section>
  </Layout>
);

export default MarketStructure;
